import {
  DetailBox,
  Icon,
  TextInput,
  TextOutlinedInput,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { SecureResponseFile } from '../../../../components/AzureFilesUploader/types';
import GoBackToListButton from '../../../../components/buttons/GoBackToListButton';
import { SectionTitle } from '../../../../components/text';
import { TranslationsKeys } from '../../../../locales/translations';
import ValidationFileChip from '../../../CourseValidationInternalStudent/parts/ModalCreateValidation/ValidationFileChip';
import { StudentsRemovalRequestsPendingDetailsResponse } from '../types';

interface Props {
  data: StudentsRemovalRequestsPendingDetailsResponse;
}

export function StudentsRemovalRequestPendingSummary({ data }: Props) {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_MANAGEMENT_DETAILS,
  );

  const texts = useMemo(
    () => ({
      title: t('title'),
      description: t('description'),
      infoStudent: t('infoBox.student'),
      infoStudentId: t('infoBox.studentId'),
      infoStudyPlan: t('infoBox.studyPlanName'),
      motive: t('form.motive'),
      removalRequestType: t('form.removalRequestType'),
      complementaryFiles: t('form.complementaryFiles'),
      noComplementaryFiles: t('form.noComplementaryFiles'),
      requestedBy: t('form.requestedBy'),
    }),
    [t],
  );

  const handleDownloadFile = useCallback((file: SecureResponseFile) => {
    if (!file?.url) {
      return;
    }
    window.open(file?.url, '_blank');
  }, []);

  const formatDate = (date: Date) => dayjs(date).format('DD/MM/YYYY');

  return (
    <>
      <Row>
        <Col xs={12} md={6} lg={4} className="d-flex align-items-center">
          <GoBackToListButton />
        </Col>
        <Col xs={12} md={6} lg={8} className="d-flex justify-content-end">
          <DetailBox
            columns={[
              {
                title: texts.infoStudent,
                body: `${data.student.accountNames} ${data.student.accountLastNames}`,
              },
              {
                title: texts.infoStudentId,
                body: `${data.student.identificationTypeName} ${data.student.identificationValueFormatted}`,
              },
              {
                title: texts.infoStudyPlan,
                body: data.studyPlanVersion.name,
              },
            ]}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xs={12}>
          <SectionTitle text={texts.title} />
          <p>{texts.description}</p>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xs={12} md={4}>
          <TextInput
            name="removalRequestType"
            label={texts.removalRequestType}
            value={data.studentRemovalRequest.type}
            readOnly
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xs={12}>
          <p className="fs-16 text-uppercase text-primary fw-700 mb-0">
            {texts.motive}
          </p>
        </Col>
        <Col xs={12} className="mt-3">
          <TextOutlinedInput
            name="motive"
            value={data.studentRemovalRequest.comment}
            readOnly
            disabled
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xs={12}>
          <p className="fs-16 fw-500 mb-0 text-dark">
            {texts.complementaryFiles}
          </p>
          {!!data.studentRemovalRequest.files?.length ? (
            <div
              className="w-full mt-3 d-flex flex-wrap align-items-center justify-content-start"
              style={{ gap: 10, boxShadow: 'inherit' }}
            >
              {data.studentRemovalRequest.files?.map((file: any, i: number) => (
                <ValidationFileChip
                  key={file.id.toString()}
                  value={i}
                  children={`${file.name}.${file.extension}`}
                  onDownload={() => handleDownloadFile(file)}
                />
              ))}
            </div>
          ) : (
            <p className="mt-3">{texts.noComplementaryFiles}</p>
          )}
        </Col>
      </Row>
      <Row className="mt-4">
        <Col
          xs={12}
          className="d-flex flex-row align-items-center justify-content-start"
        >
          <Icon name="information" size={14} className="mr-2" />
          <p className="fw-600 text-dark mr-2 mb-0">{texts.requestedBy}</p>
          <p className="mr-2 mb-0">{`${data.studentRemovalRequest.requestedBy.accountNames} ${data.studentRemovalRequest.requestedBy.accountLastNames}`}</p>
          <p className="mb-0">{`${formatDate(
            data.studentRemovalRequest.requestedAt,
          )}`}</p>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <hr className="my-4" />
        </Col>
      </Row>
    </>
  );
}
